import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SiteHeader } from "../components/platform/PlatformChrome";
import { Footer } from "../components/landing/Footer";
import { YoutubePlayer } from "../components/youtube/YoutubePlayer";
import {
  formatDuration,
  formatViewCount,
  useYoutubeLibrary,
  useYoutubePlaylists,
  videoWatchUrl,
  VIDEO_SORT_LABELS,
  type VideoSort,
  type YoutubeLibraryVideo,
  type YoutubePlaylistSummary,
} from "../lib/youtube";

/**
 * Public-facing library at /youtube. Two views:
 *   ?view=videos (default) — paginated, sortable, searchable grid
 *   ?view=series           — playlist grid; clicking opens an embed in place
 *
 * Library controls (above the grid):
 *   • Sort dropdown — newest, oldest, longest, shortest, popular, A→Z
 *   • Grid density — comfortable (240px min) or compact (180px min)
 *   • Search       — title + description LIKE
 *
 * The page uses the editorial register (display serif page title, vellum
 * background, italic flourishes) — same vocabulary as /news and /calendar.
 */

const VALID_SORTS: VideoSort[] = ["newest", "oldest", "longest", "shortest", "popular", "alpha"];
type Density = "comfortable" | "compact";
const DENSITY_PAGE_SIZE: Record<Density, number> = {
  comfortable: 18,
  compact: 36,
};

type View = "videos" | "series";

export default function PublicYoutube() {
  const [params, setParams] = useSearchParams();
  const view = (params.get("view") as View) || "videos";
  const q = params.get("q") ?? "";
  const sortParam = params.get("sort") as VideoSort | null;
  const sort: VideoSort =
    sortParam && VALID_SORTS.includes(sortParam) ? sortParam : "newest";
  const density: Density =
    params.get("density") === "compact" ? "compact" : "comfortable";
  const offset = Math.max(0, Number(params.get("offset") ?? "0"));

  const [qInput, setQInput] = useState(q);
  useEffect(() => setQInput(q), [q]);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "offset") next.delete("offset");
    setParams(next, { replace: true });
  };

  const pageSize = DENSITY_PAGE_SIZE[density];

  return (
    <>
      <SiteHeader />
      <main className="pt-28 md:pt-32 pb-24 min-h-[80svh]">
        <article className="container-edit">
          <header className="mb-10 md:mb-14">
            <p className="section-heading-num tabular mb-3">∞ / Library</p>
            <h1 className="font-display text-[clamp(2.4rem,6vw,4.75rem)] leading-[1.04] tracking-[-0.02em] max-w-[20ch]">
              Every <span className="font-display-italic">recording</span>,
              gathered.
            </h1>
            <p className="mt-5 max-w-[55ch] text-[1.05rem] leading-[1.65] text-[var(--color-ink-soft)]">
              Classes, full Vedic recitations, evenings of bhajan, and footage
              from yajñas — surfaced in our own register, not YouTube's. Each
              piece links back to the source.
            </p>
          </header>

          <nav
            className="flex flex-wrap items-baseline gap-x-6 gap-y-2 border-b border-[var(--color-rule)] mb-7"
            aria-label="Library views"
          >
            <button
              type="button"
              className={`subtab ${view === "videos" ? "is-active" : ""}`}
              onClick={() => setParam("view", null)}
            >
              Videos
            </button>
            <button
              type="button"
              className={`subtab ${view === "series" ? "is-active" : ""}`}
              onClick={() => setParam("view", "series")}
            >
              Series
            </button>
            <span className="ml-auto font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)]">
              Read directly from{" "}
              <Link
                to="/"
                className="underline decoration-[var(--color-rule-violet)] underline-offset-[3px] hover:text-[var(--color-violet)] transition-colors"
              >
                vedaunion.org
              </Link>
            </span>
          </nav>

          {view === "videos" ? (
            <VideosView
              q={q}
              qInput={qInput}
              setQInput={setQInput}
              sort={sort}
              density={density}
              pageSize={pageSize}
              offset={offset}
              onParam={setParam}
            />
          ) : (
            <SeriesView
              density={density}
              pageSize={pageSize}
              offset={offset}
              onParam={setParam}
            />
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}

// ---------------------------------------------------------------------------

function LibraryToolbar({
  qInput,
  setQInput,
  q,
  onParam,
  sort,
  density,
  showSort,
  showSearch,
}: {
  qInput: string;
  setQInput: (s: string) => void;
  q: string;
  onParam: (k: string, v: string | null) => void;
  sort: VideoSort;
  density: Density;
  showSort: boolean;
  showSearch: boolean;
}) {
  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    onParam("q", qInput.trim() || null);
  };
  return (
    <div className="yt-public-toolbar">
      {showSearch && (
        <form onSubmit={onSearch} className="yt-search yt-public-search">
          <input
            type="search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search class titles, mantras, occasions…"
            aria-label="Search videos"
          />
          <button type="submit" className="btn-app-ghost">
            Search
          </button>
          {q && (
            <button
              type="button"
              className="row-action"
              onClick={() => {
                setQInput("");
                onParam("q", null);
              }}
            >
              Clear
            </button>
          )}
        </form>
      )}

      <div className="yt-public-controls">
        {showSort && (
          <label className="chip chip-select">
            <span className="chip-label">Sort</span>
            <select
              value={sort}
              onChange={(e) =>
                onParam("sort", e.target.value === "newest" ? null : e.target.value)
              }
              aria-label="Sort"
            >
              {VALID_SORTS.map((s) => (
                <option key={s} value={s}>
                  {VIDEO_SORT_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
        )}

        <fieldset className="yt-density">
          <legend className="sr-only">Density</legend>
          <button
            type="button"
            className={`yt-density-btn ${density === "comfortable" ? "is-active" : ""}`}
            onClick={() => onParam("density", null)}
            aria-pressed={density === "comfortable"}
            title="Comfortable density — bigger thumbnails"
          >
            <span aria-hidden>▢▢</span>
            <span className="sr-only">Comfortable</span>
          </button>
          <button
            type="button"
            className={`yt-density-btn ${density === "compact" ? "is-active" : ""}`}
            onClick={() => onParam("density", "compact")}
            aria-pressed={density === "compact"}
            title="Compact density — more thumbnails per row"
          >
            <span aria-hidden>▢▢▢</span>
            <span className="sr-only">Compact</span>
          </button>
        </fieldset>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function VideosView({
  q,
  qInput,
  setQInput,
  sort,
  density,
  pageSize,
  offset,
  onParam,
}: {
  q: string;
  qInput: string;
  setQInput: (s: string) => void;
  sort: VideoSort;
  density: Density;
  pageSize: number;
  offset: number;
  onParam: (k: string, v: string | null) => void;
}) {
  const queryRes = useYoutubeLibrary({ limit: pageSize, offset, q, sort });
  const videos = queryRes.data?.videos ?? [];
  const total = queryRes.data?.total ?? 0;

  return (
    <>
      <LibraryToolbar
        qInput={qInput}
        setQInput={setQInput}
        q={q}
        onParam={onParam}
        sort={sort}
        density={density}
        showSort
        showSearch
      />

      <p className="yt-admin-count yt-public-count">
        {queryRes.isLoading ? (
          "Loading…"
        ) : total === 0 ? (
          q ? "No videos match." : "No videos yet."
        ) : (
          <>
            <span className="tabular">{total.toLocaleString()}</span>
            {q ? " match" : " video"}
            {total === 1 ? "" : q ? "es" : "s"} ·{" "}
            {VIDEO_SORT_LABELS[sort].toLowerCase()}
          </>
        )}
      </p>

      {queryRes.isLoading ? (
        <PublicGridSkeleton count={pageSize} density={density} />
      ) : videos.length === 0 ? null : (
        <ul className={`yt-public-grid is-${density}`}>
          {videos.map((v) => (
            <PublicVideoCard key={v.id} video={v} />
          ))}
        </ul>
      )}

      <PublicPagination
        offset={offset}
        pageSize={pageSize}
        total={total}
        onChange={(o) => onParam("offset", o > 0 ? String(o) : null)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------

function SeriesView({
  density,
  pageSize,
  offset,
  onParam,
}: {
  density: Density;
  pageSize: number;
  offset: number;
  onParam: (k: string, v: string | null) => void;
}) {
  const queryRes = useYoutubePlaylists({ limit: pageSize, offset });
  const playlists = queryRes.data?.playlists ?? [];
  const total = queryRes.data?.total ?? 0;

  return (
    <>
      <LibraryToolbar
        qInput=""
        setQInput={() => undefined}
        q=""
        onParam={onParam}
        sort="newest"
        density={density}
        showSort={false}
        showSearch={false}
      />

      <p className="yt-admin-count yt-public-count">
        {queryRes.isLoading
          ? "Loading…"
          : total === 0
            ? "No series yet."
            : `${total.toLocaleString()} series`}
      </p>

      {queryRes.isLoading ? (
        <PublicGridSkeleton count={pageSize} density={density} />
      ) : playlists.length === 0 ? null : (
        <ul className={`yt-public-grid is-${density}`}>
          {playlists.map((p) => (
            <PublicPlaylistCard key={p.id} playlist={p} />
          ))}
        </ul>
      )}
      <PublicPagination
        offset={offset}
        pageSize={pageSize}
        total={total}
        onChange={(o) => onParam("offset", o > 0 ? String(o) : null)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------

function PublicVideoCard({ video }: { video: YoutubeLibraryVideo }) {
  const [active, setActive] = useState(false);
  return (
    <li className="yt-card">
      <div className="yt-card-frame">
        {active ? (
          <YoutubePlayer kind="video" id={video.id} title={video.title} />
        ) : (
          <button
            type="button"
            className="yt-card-poster"
            onClick={() => setActive(true)}
            aria-label={`Play ${video.title}`}
          >
            <img
              src={video.thumbnailUrl}
              alt=""
              loading="lazy"
              decoding="async"
            />
            {video.durationSec ? (
              <span className="yt-card-duration tabular" aria-hidden>
                {formatDuration(video.durationSec)}
              </span>
            ) : null}
            <span className="yt-play" aria-hidden>
              <span>▶</span>
            </span>
          </button>
        )}
      </div>
      <div className="yt-card-meta">
        <a
          href={videoWatchUrl(video.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="font-display text-[1.1rem] sm:text-[1.2rem] leading-[1.22] tracking-[-0.005em] hover:text-[var(--color-violet)] transition-colors"
        >
          {video.title}
        </a>
        <p className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)] mt-1.5">
          {video.publishedAt && formatPublished(video.publishedAt)}
          {video.viewCount !== null && video.viewCount !== undefined && (
            <>
              <span className="mx-2 text-[var(--color-ink-light)]">·</span>
              {formatViewCount(video.viewCount)} views
            </>
          )}
        </p>
      </div>
    </li>
  );
}

function PublicPlaylistCard({ playlist }: { playlist: YoutubePlaylistSummary }) {
  const [active, setActive] = useState(false);
  const count = playlist.itemCount;
  return (
    <li className="yt-card is-playlist">
      <div className="yt-card-frame">
        {active ? (
          <YoutubePlayer
            kind="playlist"
            id={playlist.id}
            title={playlist.title}
          />
        ) : (
          <>
            <span className="yt-pl-badge" aria-hidden>
              <span>Series</span>
              <span className="yt-pl-badge-num">{count > 0 ? count : "—"}</span>
            </span>
            <span className="yt-pl-meta" aria-hidden>
              Open series →
            </span>
            <button
              type="button"
              className="yt-card-poster"
              onClick={() => setActive(true)}
              aria-label={`Play ${playlist.title} (${count} ${count === 1 ? "video" : "videos"})`}
            >
              {playlist.thumbnailUrl ? (
                <img
                  src={playlist.thumbnailUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <span className="yt-row-thumb-empty">—</span>
              )}
              <span className="yt-stack-mark" aria-hidden />
              <span className="yt-play" aria-hidden>
                <span>▶</span>
              </span>
            </button>
          </>
        )}
      </div>
      <div className="yt-card-meta">
        <a
          href={`https://www.youtube.com/playlist?list=${playlist.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="yt-pl-title font-display text-[1.15rem] sm:text-[1.25rem] leading-[1.2] hover:text-[var(--color-violet)] transition-colors"
        >
          {playlist.title}
        </a>
        <p className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)] mt-1.5">
          {count > 0
            ? `${count} ${count === 1 ? "video" : "videos"} · series`
            : "series"}
        </p>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------

function PublicPagination({
  offset,
  pageSize,
  total,
  onChange,
}: {
  offset: number;
  pageSize: number;
  total: number;
  onChange: (offset: number) => void;
}) {
  if (total <= pageSize) return null;
  return (
    <nav className="yt-pagination yt-public-pagination" aria-label="Pagination">
      <button
        type="button"
        className="btn-ghost"
        onClick={() => onChange(Math.max(0, offset - pageSize))}
        disabled={offset <= 0}
      >
        <span>Newer</span>
      </button>
      <span className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)]">
        Page {Math.floor(offset / pageSize) + 1} of{" "}
        {Math.max(1, Math.ceil(total / pageSize))}
      </span>
      <button
        type="button"
        className="btn-ghost"
        onClick={() => onChange(offset + pageSize)}
        disabled={offset + pageSize >= total}
      >
        <span>Older</span>
      </button>
    </nav>
  );
}

function PublicGridSkeleton({
  count,
  density,
}: {
  count: number;
  density: Density;
}) {
  return (
    <ul className={`yt-public-grid is-${density}`}>
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="yt-card">
          <div className="yt-card-frame">
            <div className="skel-block w-full h-full" aria-hidden />
          </div>
          <div className="yt-card-meta">
            <div
              className="skel-block"
              style={{ width: "82%", height: 16, marginBottom: 8 }}
            />
            <div
              className="skel-block"
              style={{ width: "40%", height: 10 }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function formatPublished(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}
